// Carrinho.jsx - Componente White-Label
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import styles from '../css/comuns/Carrinho.module.css';
import { config } from '../../data';

const Carrinho = () => {
    const [cartItems, setCartItems] = useState({});
    const [activeTab, setActiveTab] = useState('planos');
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // Definição dos dados dos cards
    const cardData = {
        planos: config.content.carrinho.planos,
        internet: config.content.carrinho.internet,
        telefonia: config.content.carrinho.telefonia,
        entretenimento: config.content.carrinho.entretenimento,
    };

    // Função para obter data formatada
    const getFormattedDate = () => {
        const now = new Date();
        return `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    };

    // Adicionar ao carrinho - com log para depuração
    const addToCart = (id, name, price) => {
        setCartItems(prevItems => {
            console.log('Estado anterior:', prevItems);

            // Crie uma cópia profunda do objeto anterior
            const newItems = JSON.parse(JSON.stringify(prevItems));

            if (newItems[id]) {
                // Incrementa a quantidade em exatamente 1
                newItems[id].quantity = newItems[id].quantity + 1;
                console.log(`Incrementando ${id} para ${newItems[id].quantity}`);
                setToastMessage(`Quantidade de ${name} aumentada para ${newItems[id].quantity}!`);
            } else {
                // Adiciona um novo item com quantidade 1
                newItems[id] = {
                    name: name,
                    price: price,
                    quantity: 1
                };
                console.log(`Adicionando novo item ${id} com quantidade 1`);
                setToastMessage(`${name} adicionado ao carrinho!`);
            }

            console.log('Novo estado:', newItems);
            setShowToast(true);
            return newItems;
        });
    };

    // Remover do carrinho
    const removeFromCart = (id) => {
        setCartItems(prevItems => {
            const newItems = { ...prevItems };
            const name = newItems[id].name;
            delete newItems[id];

            setToastMessage(`${name} removido do carrinho!`);
            setShowToast(true);

            return newItems;
        });
    };

    // Aumentar quantidade - função corrigida
    const increaseQuantity = (id) => {
        setCartItems(prevItems => {
            // Criar uma cópia profunda para evitar qualquer problema de referência
            const newItems = JSON.parse(JSON.stringify(prevItems));

            // Incrementar a quantidade em exatamente 1
            newItems[id].quantity = newItems[id].quantity + 1;

            // Registrar para depuração
            console.log(`Aumentando quantidade de ${id} para ${newItems[id].quantity}`);

            return newItems;
        });
    };

    // Diminuir quantidade - função corrigida
    const decreaseQuantity = (id) => {
        setCartItems(prevItems => {
            // Criar uma cópia profunda para evitar qualquer problema de referência
            const newItems = JSON.parse(JSON.stringify(prevItems));

            if (newItems[id].quantity > 1) {
                // Decrementar a quantidade em exatamente 1
                newItems[id].quantity = newItems[id].quantity - 1;
                console.log(`Diminuindo quantidade de ${id} para ${newItems[id].quantity}`);
            } else {
                // Remover o item se a quantidade for 1
                console.log(`Removendo item ${id} do carrinho`);
                delete newItems[id];
            }

            return newItems;
        });
    };

    // Limpar carrinho
    const clearCart = () => {
        setCartItems({});
        setToastMessage('Carrinho limpo!');
        setShowToast(true);
    };

    // Preparar dados do pedido
    const prepareOrderData = () => {
        const orderData = [];
        let subtotal = 0;
        let hasInternet = false;
        let hasTelefonia = false;

        // Adicionar itens
        for (const id in cartItems) {
            if (id.startsWith('internet')) hasInternet = true;
            if (id.startsWith('tel')) hasTelefonia = true;

            const item = cartItems[id];
            const itemTotal = item.price * item.quantity;
            subtotal += itemTotal;

            orderData.push({
                produto: item.name,
                quantidade: item.quantity,
                precoUnitario: `R$ ${item.price.toFixed(2)}`,
                total: `R$ ${itemTotal.toFixed(2)}`
            });
        }

        // Calcular desconto se aplicável
        let discount = 0;
        if (hasInternet && hasTelefonia) {
            discount = subtotal * 0.1; // 10% de desconto
        }

        const total = subtotal - discount;

        return {
            items: orderData,
            subtotal: subtotal,
            discount: discount,
            total: total,
            hasDiscount: hasInternet && hasTelefonia
        };
    };

    // Exportar para PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        const orderData = prepareOrderData();
        const formattedDate = getFormattedDate();

        // URL da logo da empresa
        const logoUrl = config.images.logos.main;

        // Função para adicionar a logo e continuar com a geração do PDF
        function addLogoAndContinue(logoImg) {
            // Adicionar logo (posição X, Y, largura, altura)
            if (logoImg) {
                doc.addImage(logoImg, 'PNG', 15, 10, 40, 20);
            }

            // Adicionar cabeçalho
            doc.setFontSize(20);
            doc.text('Orçamento', 105, 20, { align: 'center' });

            doc.setFontSize(10);
            doc.text(`Data: ${formattedDate}`, 105, 30, { align: 'center' });
            doc.text('Este documento é apenas um orçamento e não representa uma compra finalizada.', 105, 35, { align: 'center' });

            // Adicionar tabela de produtos
            doc.setFontSize(12);
            doc.text('Produtos Selecionados:', 14, 45);

            const tableColumn = ["Produto", "Quantidade", "Preço Unitário", "Total"];
            const tableRows = [];

            orderData.items.forEach(item => {
                const itemData = [
                    item.produto,
                    item.quantidade,
                    item.precoUnitario,
                    item.total
                ];
                tableRows.push(itemData);
            });

            // Importar e usar autoTable
            import('jspdf-autotable').then((autoTableModule) => {
                const autoTable = autoTableModule.default;

                // Definir uma posição inicial para a tabela
                let startY = 50;

                // Aplicar a tabela
                autoTable(doc, {
                    head: [tableColumn],
                    body: tableRows,
                    startY: startY,
                    theme: 'grid',
                    styles: { fontSize: 10 },
                    headStyles: { fillColor: [130, 28, 229] },
                    // Capturar a posição final da tabela usando didDrawPage
                    didDrawPage: (data) => {
                        startY = data.cursor.y;
                    }
                });

                // Usar a posição capturada para o próximo conteúdo
                // Adicionar um espaço extra
                startY += 10;

                // Adicionar resumo do pedido
                doc.text('Resumo do Pedido:', 14, startY);
                doc.text(`Subtotal: R$ ${orderData.subtotal.toFixed(2)}`, 14, startY + 7);

                if (orderData.hasDiscount) {
                    doc.text(`Desconto (10% Combo Internet + Telefonia): -R$ ${orderData.discount.toFixed(2)}`, 14, startY + 14);
                    doc.text(`Total: R$ ${orderData.total.toFixed(2)}`, 14, startY + 21);
                } else {
                    doc.text(`Total: R$ ${orderData.total.toFixed(2)}`, 14, startY + 14);
                }

                // Adicionar rodapé
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.text(`${config.texts.company.name} - Entre em contato conosco para finalizar seu pedido`, 105, 287, { align: 'center' });
                }

                // Salvar o PDF
                doc.save(`${config.client.id}_Orcamento.pdf`);

                // Mostrar notificação
                setToastMessage('Orçamento exportado como PDF!');
                setShowToast(true);
            });
        }

        // Carregar a imagem e depois gerar o PDF
        const logoImg = new Image();
        logoImg.crossOrigin = 'Anonymous';  // Para evitar problemas de CORS
        logoImg.onload = function () {
            addLogoAndContinue(logoImg);
        };
        logoImg.onerror = function () {
            // Se a imagem falhar, continue sem a logo
            console.error('Não foi possível carregar a logo. Gerando PDF sem logo.');
            addLogoAndContinue(null);
        };
        logoImg.src = logoUrl;
    };

    // Exportar para Excel
    const exportToExcel = () => {
        const orderData = prepareOrderData();
        const formattedDate = getFormattedDate();

        // Criar planilha
        const wb = XLSX.utils.book_new();

        // Dados dos produtos
        const productData = [
            [`${config.texts.company.name} - Orçamento`, '', '', ''],
            [`Data: ${formattedDate}`, '', '', ''],
            ['Este documento é apenas um orçamento e não representa uma compra finalizada.', '', '', ''],
            ['', '', '', ''],
            ['Produtos Selecionados:', '', '', ''],
            ['Produto', 'Quantidade', 'Preço Unitário', 'Total']
        ];

        // Adicionar itens
        orderData.items.forEach(item => {
            productData.push([
                item.produto,
                item.quantidade,
                item.precoUnitario,
                item.total
            ]);
        });

        // Adicionar linha em branco
        productData.push(['', '', '', '']);

        // Adicionar resumo
        productData.push(['Resumo do Pedido:', '', '', '']);
        productData.push(['Subtotal:', '', '', `R$ ${orderData.subtotal.toFixed(2)}`]);

        if (orderData.hasDiscount) {
            productData.push(['Desconto (10% Combo Internet + Telefonia):', '', '', `-R$ ${orderData.discount.toFixed(2)}`]);
        }

        productData.push(['Total:', '', '', `R$ ${orderData.total.toFixed(2)}`]);

        // Adicionar rodapé
        productData.push(['', '', '', '']);
        productData.push([`${config.texts.company.name} - Entre em contato conosco para finalizar seu pedido`, '', '', '']);

        // Criar planilha e salvar
        const ws = XLSX.utils.aoa_to_sheet(productData);

        // Configurar largura das colunas
        const cols = [
            { wch: 40 }, // A
            { wch: 15 }, // B
            { wch: 15 }, // C
            { wch: 15 }  // D
        ];
        ws['!cols'] = cols;

        XLSX.utils.book_append_sheet(wb, ws, "Orçamento");

        // Salvar arquivo
        XLSX.writeFile(wb, `${config.client.id}_Orcamento.xlsx`);

        // Mostrar notificação
        setToastMessage('Orçamento exportado como Excel!');
        setShowToast(true);
    };

    // Calcular total e verificar descontos
    const calculateTotal = () => {
        let total = 0;
        let itemsCount = 0;
        let hasInternet = false;
        let hasTelefonia = false;

        for (const id in cartItems) {
            if (id.startsWith('internet')) hasInternet = true;
            if (id.startsWith('tel')) hasTelefonia = true;

            const item = cartItems[id];
            itemsCount += item.quantity;
            total += item.price * item.quantity;
        }

        // Aplicar desconto se tiver internet + telefonia
        const hasDiscount = hasInternet && hasTelefonia;
        const discount = hasDiscount ? total * 0.1 : 0; // 10% de desconto
        const finalTotal = total - discount;

        return {
            subtotal: total,
            discount: discount,
            total: finalTotal,
            itemsCount: itemsCount,
            hasDiscount: hasDiscount
        };
    };

    // Fechar toast após 3 segundos
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // Calcular valores do carrinho
    const { subtotal, discount, total, itemsCount, hasDiscount } = calculateTotal();
    const hasItems = Object.keys(cartItems).length > 0;

    return (
        <div className="container py-4">
            {/* Título do Carrinho */}
            <div className="row mb-4">
                <div className="col-12 text-center">
                    <h2 className={`animate__animated animate__fadeInDown ${styles['title']}`}>
                        <i className="bi bi-cart3"></i> Carrinho {config.texts.company.name}
                    </h2>
                    <p className="text-muted animate__animated animate__fadeIn">Escolha seus produtos e monte seu pacote ideal</p>
                    <hr />
                </div>
            </div>

            <div className="row">
                {/* Produtos Disponíveis */}
                <div className="col-lg-8">
                    <h4 className="mb-4"><i className="bi bi-bag-plus"></i> Adicione produtos ao seu carrinho</h4>

                    {/* Categorias em abas */}
                    <ul className="nav nav-tabs mb-3" id="productTabs" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button
                                className={`nav-link ${activeTab === 'planos' ? 'active' : ''}`}
                                onClick={() => setActiveTab('planos')}
                            >
                                <i className={`bi bi-box ${styles['category-icon']}`}></i>Planos
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button
                                className={`nav-link ${activeTab === 'internet' ? 'active' : ''}`}
                                onClick={() => setActiveTab('internet')}
                            >
                                <i className={`bi bi-wifi ${styles['category-icon']}`}></i>Internet
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button
                                className={`nav-link ${activeTab === 'telefonia' ? 'active' : ''}`}
                                onClick={() => setActiveTab('telefonia')}
                            >
                                <i className={`bi bi-telephone ${styles['category-icon']}`}></i>Telefonia
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button
                                className={`nav-link ${activeTab === 'entretenimento' ? 'active' : ''}`}
                                onClick={() => setActiveTab('entretenimento')}
                            >
                                <i className={`bi bi-film ${styles['category-icon']}`}></i>Entretenimento
                            </button>
                        </li>
                    </ul>

                    {/* Conteúdo das abas */}
                    <div className="tab-content">
                        {/* Planos */}
                        {activeTab === 'planos' && (
                            <div className="row">
                                {cardData.planos.map(card => (
                                    <div key={card.id} className="col-md-6 col-lg-4 mb-4">
                                        <div className={styles['product-card']}>
                                            <div
                                                className={styles['product-image']}
                                                style={{ backgroundImage: `url("${card.image}")` }}
                                            ></div>
                                            {card.badge && (
                                                <span className={styles['product-badge']}>{card.badge}</span>
                                            )}
                                            <div className="card-body">
                                                <h5 className={styles['product-title']}>{card.name}</h5>
                                                <p className={styles['product-description']}>{card.description}</p>
                                                <p className={styles['product-price']}>R$ {card.price.toFixed(2)}<small>/mês</small></p>
                                                <button
                                                    className={`btn ${styles['oni-gradient']} w-100`}
                                                    onClick={() => addToCart(card.id, card.name, card.price)}
                                                >
                                                    <i className="bi bi-cart-plus"></i> Adicionar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Internet */}
                        {activeTab === 'internet' && (
                            <div className="row">
                                {cardData.internet.map(card => (
                                    <div key={card.id} className="col-md-6 col-lg-4 mb-4">
                                        <div className={styles['product-card']}>
                                            <div
                                                className={styles['product-image']}
                                                style={{ backgroundImage: `url("${card.image}")` }}
                                            ></div>
                                            {card.badge && (
                                                <span className={styles['product-badge']}>{card.badge}</span>
                                            )}
                                            <div className="card-body">
                                                <h5 className={styles['product-title']}>{card.name}</h5>
                                                <p className={styles['product-description']}>{card.description}</p>
                                                <p className={styles['product-price']}>R$ {card.price.toFixed(2)}<small>/mês</small></p>
                                                <button
                                                    className={`btn ${styles['oni-gradient']} w-100`}
                                                    onClick={() => addToCart(card.id, card.name, card.price)}
                                                >
                                                    <i className="bi bi-cart-plus"></i> Adicionar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Telefonia */}
                        {activeTab === 'telefonia' && (
                            <div className="row">
                                {cardData.telefonia.map(card => (
                                    <div key={card.id} className="col-md-6 col-lg-4 mb-4">
                                        <div className={styles['product-card']}>
                                            <div
                                                className={styles['product-image']}
                                                style={{ backgroundImage: `url("${card.image}")` }}
                                            ></div>
                                            {card.badge && (
                                                <span className={styles['product-badge']}>{card.badge}</span>
                                            )}
                                            <div className="card-body">
                                                <h5 className={styles['product-title']}>{card.name}</h5>
                                                <p className={styles['product-description']}>{card.description}</p>
                                                <p className={styles['product-price']}>R$ {card.price.toFixed(2)}<small>/mês</small></p>
                                                <button
                                                    className={`btn ${styles['oni-gradient']} w-100`}
                                                    onClick={() => addToCart(card.id, card.name, card.price)}
                                                >
                                                    <i className="bi bi-cart-plus"></i> Adicionar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Entretenimento */}
                        {activeTab === 'entretenimento' && (
                            <div className="row">
                                {cardData.entretenimento.map(card => (
                                    <div key={card.id} className="col-md-6 col-lg-4 mb-4">
                                        <div className={styles['product-card']}>
                                            <div
                                                className={styles['product-image']}
                                                style={{ backgroundImage: `url("${card.image}")` }}
                                            ></div>
                                            {card.badge && (
                                                <span className={styles['product-badge']}>{card.badge}</span>
                                            )}
                                            <div className="card-body">
                                                <h5 className={styles['product-title']}>{card.name}</h5>
                                                <p className={styles['product-description']}>{card.description}</p>
                                                <p className={styles['product-price']}>R$ {card.price.toFixed(2)}<small>/mês</small></p>
                                                <button
                                                    className={`btn ${styles['oni-gradient']} w-100`}
                                                    onClick={() => addToCart(card.id, card.name, card.price)}
                                                >
                                                    <i className="bi bi-cart-plus"></i> Adicionar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Carrinho */}
                <div className="col-lg-4">
                    <div className={`card ${styles['cart-container']}`}>
                        <div className={`card-header ${styles['oni-gradient']} ${styles['cart-header']}`}>
                            <h5 className="mb-0"><i className="bi bi-cart3"></i> Seu Carrinho</h5>
                        </div>
                        <div className="card-body">
                            <div id="cart-items">
                                {!hasItems ? (
                                    <div className={styles['empty-cart']}>
                                        <i className="bi bi-cart"></i>
                                        <p>Seu carrinho está vazio.</p>
                                        <p className="text-muted">Adicione produtos para continuar</p>
                                    </div>
                                ) : (
                                    <>
                                        {Object.keys(cartItems).map(id => {
                                            const item = cartItems[id];
                                            const itemTotal = item.price * item.quantity;

                                            return (
                                                <div key={id} className={`${styles['cart-item']} p-2 mb-2 animate__animated animate__fadeIn`}>
                                                    <div className="d-flex justify-content-between">
                                                        <div>
                                                            <h6 className="mb-0">{item.name}</h6>
                                                            <small className="text-muted">R$ {item.price.toFixed(2)}</small>
                                                            <div className={styles['quantity-control']}>
                                                                <button
                                                                    className={styles['quantity-btn']}
                                                                    onClick={() => decreaseQuantity(id)}
                                                                >
                                                                    -
                                                                </button>
                                                                <span className={styles['quantity-value']}>{item.quantity}</span>
                                                                <button
                                                                    className={styles['quantity-btn']}
                                                                    onClick={() => increaseQuantity(id)}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <span className="me-2 fw-bold">R$ {itemTotal.toFixed(2)}</span>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => removeFromCart(id)}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {hasDiscount && (
                                            <div className="alert alert-success mt-2 mb-2 animate__animated animate__fadeIn">
                                                <div className="d-flex justify-content-between">
                                                    <span><i className="bi bi-tag"></i> Desconto Combo (10%):</span>
                                                    <span>-R$ {discount.toFixed(2)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className={styles['cart-total-section']}>
                                <div className="d-flex justify-content-between">
                                    <h5>Total:</h5>
                                    <h5>R$ {total.toFixed(2)}</h5>
                                </div>
                                <div className="d-flex justify-content-between text-muted small mb-2">
                                    <span>Itens:</span>
                                    <span>{itemsCount}</span>
                                </div>
                            </div>

                            <button
                                className={`btn ${styles['oni-gradient']} w-100 mt-3`}
                                disabled={!hasItems}
                                onClick={() => setShowCheckoutModal(true)}
                            >
                                <i className="bi bi-credit-card"></i> Finalizar Compra
                            </button>

                            <div className="text-center mt-3">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={!hasItems}
                                    onClick={clearCart}
                                >
                                    <i className="bi bi-trash"></i> Limpar Carrinho
                                </button>
                            </div>

                            <div className="text-center mt-3">
                                <div className="btn-group">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        disabled={!hasItems}
                                        onClick={exportToPDF}
                                    >
                                        <i className="bi bi-file-pdf"></i> Exportar PDF
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-success"
                                        disabled={!hasItems}
                                        onClick={exportToExcel}
                                    >
                                        <i className="bi bi-file-excel"></i> Exportar Excel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sugestões */}
                    <div className="card mt-4">
                        <div className={`card-header ${styles['oni-gradient']}`}>
                            <h5 className="mb-0"><i className="bi bi-lightbulb"></i> Recomendações</h5>
                        </div>
                        <div className="card-body">
                            <p>Combine nossos produtos e economize!</p>
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i> Ao adicionar Internet + Telefonia, você ganha 10% de desconto!
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de confirmação */}
            {showCheckoutModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className={`modal-header ${styles['oni-gradient']}`}>
                                <h5 className="modal-title">Finalizar Compra</h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowCheckoutModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Você está prestes a finalizar sua compra com os seguintes itens:</p>
                                <div>
                                    {Object.keys(cartItems).map(id => {
                                        const item = cartItems[id];
                                        const itemTotal = item.price * item.quantity;

                                        return (
                                            <div key={id} className="d-flex justify-content-between">
                                                <span>{item.name} ({item.quantity}x)</span>
                                                <span>R$ {itemTotal.toFixed(2)}</span>
                                            </div>
                                        );
                                    })}

                                    {hasDiscount && (
                                        <div className="text-success d-flex justify-content-between">
                                            <span><i className="bi bi-tag"></i> Desconto Combo (10%):</span>
                                            <span>-R$ {discount.toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>
                                <hr />
                                <div className="d-flex justify-content-between">
                                    <h5>Total:</h5>
                                    <h5>R$ {total.toFixed(2)}</h5>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowCheckoutModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${styles['oni-gradient']}`}
                                    onClick={() => {
                                        setShowCheckoutModal(false);
                                        setTimeout(() => {
                                            setCartItems({});
                                            setShowSuccessModal(true);
                                        }, 500);
                                    }}
                                >
                                    Confirmar Compra
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de sucesso */}
            {showSuccessModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className={`modal-header ${styles['oni-gradient']}`}>
                                <h5 className="modal-title">Compra Realizada!</h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowSuccessModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body text-center">
                                <div className={styles['success-animation']}>
                                    <i className="bi bi-check-circle-fill animate__animated animate__bounceIn"></i>
                                    <h4>Parabéns!</h4>
                                    <p>Sua compra foi realizada com sucesso.</p>
                                    <p>Em breve você receberá um e-mail com os detalhes.</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className={`btn ${styles['oni-gradient']}`}
                                    onClick={() => setShowSuccessModal(false)}
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast de notificação */}
            {showToast && (
                <div className={`position-fixed bottom-0 end-0 p-3 ${styles['toast-container']}`} style={{ zIndex: 11 }}>
                    <div className={`toast show ${styles['toast']}`} role="alert">
                        <div className={`toast-header ${styles['oni-gradient']}`}>
                            <strong className="me-auto">{config.texts.company.name}</strong>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                onClick={() => setShowToast(false)}
                            ></button>
                        </div>
                        <div className="toast-body">
                            {toastMessage}
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay para modais */}
            {(showCheckoutModal || showSuccessModal) && (
                <div className={styles['modal-backdrop']} onClick={() => {
                    setShowCheckoutModal(false);
                    setShowSuccessModal(false);
                }}></div>
            )}
        </div>
    );
};

export default Carrinho;